import React, { createContext, ReactNode, useCallback, useContext, useEffect, useMemo, useRef, useState } from 'react';
import { SalePolicy, saleRequest } from '../services/saleLinks';
import { useUserInfo } from './UserInfoContext';

export interface SalePolicyContextValue {
  error: Error | null;
  loading: boolean;
  policy: SalePolicy | null;
  refresh: () => Promise<void>;
}

export const SalePolicyContext = createContext<SalePolicyContextValue | null>(null);

interface SalePolicyState {
  error: Error | null;
  identity: string | null;
  loading: boolean;
  policy: SalePolicy | null;
}

const emptyState = (identity: string | null = null): SalePolicyState => ({
  error: null,
  identity,
  loading: false,
  policy: null,
});

export const SalePolicyProvider: React.FC<{ children: ReactNode }> = ({ children }) => {
  const { isAuthenticated, user } = useUserInfo();
  const identity = isAuthenticated ? (user?.username || user?.email || null) : null;
  const [state, setState] = useState<SalePolicyState>(() => emptyState(identity));
  const requestRef = useRef<{ identity: string; promise: Promise<void> } | null>(null);

  const load = useCallback((force = false): Promise<void> => {
    if (!identity) {
      requestRef.current = null;
      setState(emptyState());
      return Promise.resolve();
    }
    if (!force && state.identity === identity && state.policy) {
      return Promise.resolve();
    }
    if (requestRef.current?.identity === identity) {
      return requestRef.current.promise;
    }

    setState((current) => ({ ...current, error: null, identity, loading: true, policy: current.identity === identity ? current.policy : null }));
    const promise = saleRequest<SalePolicy>('/policy')
      .then((policy) => {
        setState({ error: null, identity, loading: false, policy });
      })
      .catch((error) => {
        setState({ error: error instanceof Error ? error : new Error('Failed to load sale policy'), identity, loading: false, policy: null });
        throw error;
      })
      .finally(() => {
        if (requestRef.current?.promise === promise) requestRef.current = null;
      });
    requestRef.current = { identity, promise };
    return promise;
  }, [identity, state.identity, state.policy]);

  useEffect(() => {
    if (!identity) {
      setState(emptyState());
      return;
    }
    void load().catch(() => undefined);
  }, [identity, load]);

  const refresh = useCallback(() => load(true), [load]);
  const contextValue = useMemo(() => ({
    error: state.identity === identity ? state.error : null,
    loading: Boolean(identity) && (state.identity !== identity || state.loading),
    policy: state.identity === identity ? state.policy : null,
    refresh,
  }), [identity, refresh, state]);

  return <SalePolicyContext.Provider value={contextValue}>{children}</SalePolicyContext.Provider>;
};

export const useSalePolicy = (): SalePolicyContextValue => {
  const context = useContext(SalePolicyContext);
  if (!context) throw new Error('useSalePolicy must be used within SalePolicyProvider');
  return context;
};
